import React, { useState, useEffect, useRef } from 'react';
import { User, ChevronDown, LogOut, Linkedin, Building2, Plus, Check } from 'lucide-react';

const Header = () => {
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
    const [showConnectModal, setShowConnectModal] = useState(false); 
  const [linkedinAccounts, setLinkedinAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [userName, setUserName] = useState('');
  const userDropdownRef = useRef(null);
  const accountDropdownRef = useRef(null);

  useEffect(() => {
    // Load user data
    const storedUserName = localStorage.getItem('userName');
    setUserName(storedUserName || 'User');

    // Load LinkedIn accounts
    fetchLinkedInAccounts();

    // Close dropdowns when clicking outside
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
      if (accountDropdownRef.current && !accountDropdownRef.current.contains(event.target)) {
        setShowAccountDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchLinkedInAccounts = async () => {
    try {
      const userId = localStorage.getItem('userId');
      if (!userId) return;

      const response = await fetch('https://n8n.acespireconsulting.com/webhook/get-linkedin-accounts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: parseInt(userId) })
      });

      const data = await response.json();
      
      if (data.success && data.accounts) {
        setLinkedinAccounts(data.accounts);
        
        // ✅ AUTO-SELECT LOGIC
      if (data.accounts.length > 0) {
        // Check if there's a saved account in localStorage
        const savedAccountId = localStorage.getItem('selectedLinkedInAccountId');
        
        let accountToSelect;
        
        if (savedAccountId) {
          // Try to find the saved account
          accountToSelect = data.accounts.find(a => a.linkedin_account_id === parseInt(savedAccountId));
        }
        
        // ✅ IF NO SAVED ACCOUNT OR SAVED ACCOUNT NOT FOUND, AUTO-SELECT FIRST ONE
        if (!accountToSelect) {
          // Select primary account or first account
          accountToSelect = data.accounts.find(a => a.is_primary) || data.accounts[0];
          
          console.log('🔄 Auto-selecting account:', accountToSelect.linkedin_username);
          
          // ✅ AUTOMATICALLY SET IT IN localStorage
          localStorage.setItem('selectedLinkedInAccountId', accountToSelect.linkedin_account_id);
          localStorage.setItem('selectedAccountType', accountToSelect.account_type);
          localStorage.setItem('selectedLinkedInUsername', accountToSelect.profile_name || accountToSelect.linkedin_username);
          
          // ✅ FIRE ACCOUNT CHANGED EVENT TO TRIGGER DASHBOARD LOAD
          window.dispatchEvent(new Event('accountChanged'));
        }
        
        setSelectedAccount(accountToSelect);
      } else {
        // ✅ NO ACCOUNTS CONNECTED - CLEAR EVERYTHING
        console.log('⚠️ No connected accounts found');
        setSelectedAccount(null);
        localStorage.removeItem('selectedLinkedInAccountId');
        localStorage.removeItem('selectedAccountType');
        localStorage.removeItem('selectedLinkedInUsername');
        
        // ✅ FIRE EVENT TO SHOW GUEST MODE
        window.dispatchEvent(new Event('accountChanged'));
      }
    } else {
      // ✅ EMPTY RESPONSE - CLEAR EVERYTHING
      console.log('⚠️ No accounts in response');
      setLinkedinAccounts([]);
      setSelectedAccount(null);
      localStorage.removeItem('selectedLinkedInAccountId');
      localStorage.removeItem('selectedAccountType');
      localStorage.removeItem('selectedLinkedInUsername');
      
      window.dispatchEvent(new Event('accountChanged'));
    }
  } catch (error) {
    console.error('Error fetching LinkedIn accounts:', error);
    
    // ✅ ERROR STATE - CLEAR EVERYTHING
    setLinkedinAccounts([]);
    setSelectedAccount(null);
    localStorage.removeItem('selectedLinkedInAccountId');
    localStorage.removeItem('selectedAccountType');
    localStorage.removeItem('selectedLinkedInUsername');
  }
};

  const selectAccount = (account) => {
  setSelectedAccount(account);
  localStorage.setItem('selectedLinkedInAccountId', account.linkedin_account_id);
  localStorage.setItem('selectedAccountType', account.account_type);
  localStorage.setItem('selectedLinkedInUsername', account.profile_name || account.linkedin_username); // ✅ USE PROFILE NAME
  setShowAccountDropdown(false);
  
  // Trigger page refresh to load new account data
  window.dispatchEvent(new Event('accountChanged'));
};

  // ✅ UPDATE THIS FUNCTION
  const handleConnectNewAccount = () => {
    setShowAccountDropdown(false); // Close dropdown
    setShowConnectModal(true); // Open modal
  };
  // ✅ ADD THIS FUNCTION
  const handleConnectionSuccess = async () => {
    setShowConnectModal(false);
    
    // Fetch updated accounts
    await fetchLinkedInAccounts();
    
    // Fire account changed event
    window.dispatchEvent(new Event('accountChanged'));
  };

 const handleDisconnectAccount = async (accountId) => {
  if (!window.confirm('Are you sure you want to disconnect this LinkedIn account?')) {
    return;
  }

  try {
    const response = await fetch('https://n8n.acespireconsulting.com/webhook/disconnect-linkedin', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ linkedinAccountId: accountId })
    });

    const data = await response.json();
    
    if (response.ok && data.success) {
      // ✅ IMMEDIATELY CLEAR localStorage IF THIS WAS THE SELECTED ACCOUNT
      if (selectedAccount?.linkedin_account_id === accountId) {
        localStorage.removeItem('selectedLinkedInAccountId');
        localStorage.removeItem('selectedAccountType');
        localStorage.removeItem('selectedLinkedInUsername');
      }
      
      // ✅ FIRE EVENT BEFORE RELOAD
      window.dispatchEvent(new Event('accountChanged'));
      
      alert('LinkedIn account disconnected successfully');
      
      // ✅ RELOAD PAGE TO FETCH FRESH DATA
      window.location.reload();
    } else {
      alert(data.message || 'Failed to disconnect LinkedIn account');
    }
  } catch (error) {
    console.error('Error disconnecting LinkedIn:', error);
    alert('An error occurred. Please try again.');
  }
};

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/';
  };

  return (
    <>
    <div className="flex items-center justify-between mb-4 lg:mb-6">
      <div className="flex items-center">
        <h1 className="text-xl font-semibold text-gray-800">DigiMark</h1>
      </div>
      
      <div className="flex items-center space-x-3">
        {/* Account Switcher Dropdown */}
        {linkedinAccounts.length > 0 ? (
          <div className="relative" ref={accountDropdownRef}>
            <button
              onClick={() => setShowAccountDropdown(!showAccountDropdown)}
              className="flex items-center px-3 py-2 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
            >
              {selectedAccount?.account_type === 'company' ? (
                <Building2 className="w-4 h-4 mr-2 text-blue-600" />
              ) : (
                <Linkedin className="w-4 h-4 mr-2 text-blue-600" />
              )}
              <span className="text-sm font-medium mr-2">
                {selectedAccount?.profile_name || selectedAccount?.linkedin_username || 'Select Account'}
              </span>
              <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${showAccountDropdown ? 'rotate-180' : ''}`} />
            </button>

            {showAccountDropdown && (
              <div className="absolute top-full right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                  Your LinkedIn Accounts
                </div>
                
                {linkedinAccounts.map(account => (
                  <div
                    key={account.linkedin_account_id}
                    className="px-3 py-2 hover:bg-gray-50 cursor-pointer group"
                  >
                    <div 
                      onClick={() => selectAccount(account)}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center flex-1">
                        {account.account_type === 'company' ? (
                          <Building2 className="w-4 h-4 mr-3 text-blue-600" />
                        ) : (
                          <Linkedin className="w-4 h-4 mr-3 text-blue-600" />
                        )}
                        
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {account.profile_name || account.linkedin_username || `${account.account_type} Account`}
                          </div>
                          <div className="text-xs text-gray-500 capitalize flex items-center">
                            {account.account_type}
                            {account.connection_status === 'connected' && (
                              <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                                Connected
                              </span>
                            )}
                          </div>
                        </div>

                        {selectedAccount?.linkedin_account_id === account.linkedin_account_id && (
                          <Check className="w-4 h-4 text-blue-600 ml-2" />
                        )}
                      </div>
                    </div>
                    
                    {/* Disconnect button (shows on hover) */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDisconnectAccount(account.linkedin_account_id);
                      }}
                      className="mt-1 w-full text-left px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      Disconnect
                    </button>
                  </div>
                ))}

                <div className="border-t border-gray-200 mt-2 pt-2">
                  <button
                    onClick={handleConnectNewAccount}
                    className="w-full flex items-center px-3 py-2 text-blue-600 hover:bg-blue-50 transition-colors"
                  >
                    <Plus className="w-4 h-4 mr-3" />
                    <span className="text-sm font-medium">Connect Another Account</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={handleConnectNewAccount}
            className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Linkedin className="w-4 h-4 mr-2" />
            Connect LinkedIn
          </button>
        )}

        {/* User Dropdown */}
        <div className="relative" ref={userDropdownRef}>
          <button
            onClick={() => setShowUserDropdown(!showUserDropdown)}
            className="flex items-center space-x-2 px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-white" />
            </div>
            <span className="hidden sm:inline text-sm font-medium text-gray-700">{userName}</span>
            <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${showUserDropdown ? 'rotate-180' : ''}`} />
          </button>

          {showUserDropdown && (
            <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-medium text-gray-800">{userName}</p>
                <p className="text-xs text-gray-500">{localStorage.getItem('email') || 'DigiMark User'}</p>
              </div>

              <div className="px-4 py-3">
                <p className="text-xs text-gray-500">
                  {linkedinAccounts.length} LinkedIn account{linkedinAccounts.length !== 1 ? 's' : ''} connected
                </p>
              </div>

              <button
                onClick={handleLogout}
                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Log out
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
      {/* ✅ ADD MODAL AT THE END */}
      {showConnectModal && (
        <ConnectLinkedInModal 
          onClose={() => setShowConnectModal(false)}
          onSuccess={handleConnectionSuccess}
        />
      )}
    </>
  );
};
// ✅ ADD THIS ENTIRE COMPONENT
const ConnectLinkedInModal = ({ onClose, onSuccess }) => {
  const [accountType, setAccountType] = useState('personal');
  const [linkedinEmail, setLinkedinEmail] = useState('');
  const [linkedinPassword, setLinkedinPassword] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleConnect = async (e) => {
    e.preventDefault();
    
    if (!linkedinEmail || !linkedinPassword) {
      alert('Please enter your LinkedIn credentials');
      return;
    }

    if (accountType === 'company' && !companyId) {
      alert('Please enter your company ID');
      return;
    }

    setLoading(true);

    try {
      const userId = localStorage.getItem('userId');
      
      const response = await fetch('https://n8n.acespireconsulting.com/webhook/connect-linkedin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: parseInt(userId),
          linkedinEmail,
          linkedinPassword,
          accountType,
          companyId: accountType === 'company' ? companyId : null
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        alert(`LinkedIn ${accountType} account connected successfully!`);
        
        // ✅ SET THE NEWLY CONNECTED ACCOUNT AS SELECTED
        if (data.linkedinAccountId) {
          localStorage.setItem('selectedLinkedInAccountId', data.linkedinAccountId);
          localStorage.setItem('selectedAccountType', accountType);
          localStorage.setItem('selectedLinkedInUsername', data.profileName || linkedinEmail); // ✅ USE PROFILE NAME
        }
        
        onSuccess();
      } else {
        alert(data.message || 'Failed to connect LinkedIn account');
      }
    } catch (error) {
      console.error('Error connecting LinkedIn:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Linkedin className="w-6 h-6 text-blue-600 mr-2" />
            <h2 className="text-xl font-semibold">Connect LinkedIn</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Connect your LinkedIn account to schedule and post content directly.
        </p>
<div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-gray-600">
  <strong>Note:</strong> LinkedIn may require manual verification during login.
  Please open the LinkedIn mobile app and tap <strong>“Yes, it’s me”</strong> to complete the login process.
</div>
        <form onSubmit={handleConnect}>
          {/* Account Type */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Account Type
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setAccountType('personal')}
                className={`flex items-center justify-center px-4 py-3 border-2 rounded-lg transition-colors ${
                  accountType === 'personal'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <User className="w-5 h-5 mr-2" />
                Personal
              </button>
              <button
                type="button"
                onClick={() => setAccountType('company')}
                className={`flex items-center justify-center px-4 py-3 border-2 rounded-lg transition-colors ${
                  accountType === 'company'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Building2 className="w-5 h-5 mr-2" />
                Company
              </button>
            </div>
          </div>

          {/* LinkedIn Email */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              LinkedIn Email
            </label>
            <input
              type="email"
              value={linkedinEmail}
              onChange={(e) => setLinkedinEmail(e.target.value)}
              placeholder="your.email@example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* LinkedIn Password */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              LinkedIn Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={linkedinPassword}
                onChange={(e) => setLinkedinPassword(e.target.value)}
                placeholder="Enter your password"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          {/* Company ID (only for company accounts) */}
          {accountType === 'company' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company ID
              </label>
              <input
                type="text"
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                placeholder="e.g., acespire-consulting"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Find this in your company page URL
              </p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Connecting...' : 'Connect'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Header;

// const fetchLinkedInAccounts = async () => {
//   try {
//     const userId = localStorage.getItem('userId');
//     if (!userId) return;

//     const response = await fetch('https://n8n.acespireconsulting.com/webhook/get-linkedin-accounts', {
//       method: 'POST',
//       headers: { 'Content-Type': 'application/json' },
//       body: JSON.stringify({ userId: parseInt(userId) })
//     });

//     const data = await response.json();
    
//     console.log('📊 Accounts response:', data);
    
//     if (data.success && data.accounts && data.accounts.length > 0) {
//       // ✅ VALID CONNECTED ACCOUNTS FOUND
//       setLinkedinAccounts(data.accounts);
      
//       // Load selected account from localStorage or use primary
//       const savedAccountId = localStorage.getItem('selectedLinkedInAccountId');
//       const accountToSelect = savedAccountId 
//         ? data.accounts.find(a => a.linkedin_account_id === parseInt(savedAccountId))
//         : data.accounts.find(a => a.is_primary) || data.accounts[0];
      
//       if (accountToSelect) {
//         selectAccount(accountToSelect);
//       }
//     } else {
//       // ✅ NO CONNECTED ACCOUNTS - CLEAR EVERYTHING
//       console.log('⚠️ No connected accounts found');
//       setLinkedinAccounts([]);
//       setSelectedAccount(null);
//       localStorage.removeItem('selectedLinkedInAccountId');
//       localStorage.removeItem('selectedAccountType');
//       localStorage.removeItem('selectedLinkedInUsername');
//     }
//   } catch (error) {
//     console.error('Error fetching LinkedIn accounts:', error);
//     // ✅ ON ERROR, ALSO CLEAR
//     setLinkedinAccounts([]);
//     setSelectedAccount(null);
//   }
// };